/**
 * The Product Quality teaser on the Ryerson Commitment landing page
 * (`src/pages/why-ryerson/ryerson-commitment/index.tsx`): image, title, blurb and a "More about"
 * link to `/why-ryerson/ryerson-commitment/product-quality`. Copy is in `./Content.tsx`.
 *
 * The two layouts here are reused by other commitment teasers. `./ProductQualityDesktop.tsx`
 * also lays out `../Sustainability/Sustainability.tsx` (Why Metal Matters) and
 * `../EnvironmentHealthAndSafety/EnvironmentHealthAndSafety.tsx`. `./ProductQualityMobile.tsx`
 * lays out those two and `../CertificationAndCompliance/CertificateAndComplianceMobile.tsx`, which
 * is the mobile layout of the Certification & Compliance, ESG and Ryerson Gives Back teasers, so
 * all six landing-page teasers share it on mobile. It is also imported by
 * `../RyersonGivesBackPage/RyersonGivesBackPageMobile.tsx`, whose parent `RyersonGivesBackPage.tsx`
 * nothing imports. A change to {@link ProductQualityProps} or to either layout changes all of them.
 */
import { Media } from "@ryerson/frontend.next";
import React from "react";
import { ProductQualityContent } from "./Content";
import ProductQualityDesktop from "./ProductQualityDesktop";
import ProductQualityMobile from "./ProductQualityMobile";
import { useApplication } from "@ryerson/frontend.application";

/** `ContentSection` background type for the teaser band. */
export type backgroundType = "primary" | "secondary";

/** Content of one image-beside-text teaser, rendered by `ProductQualityDesktop` / `Mobile`. */
export interface ProductQualityProps {
	/** Band background. Unset: `"secondary"` on desktop, `"primary"` on mobile. */
	background?: backgroundType;
	imageUrl: string;
	/**
	 * Heading and image alt text. Slugified (lower-cased, whitespace runs to `-`) into the
	 * teaser's anchor id. A `\n` in it renders as a space.
	 */
	title: string;
	content: string;
	/** Label beside the arrow button; `\n` breaks the line (`white-space: pre-line`). */
	buttonLabel: string;
	/** Site path the teaser links to. */
	moreAboutLink: string;
}

/** Renders the Product Quality teaser in the visitor's language, mobile below `lg`. */
const ProductQuality: React.FC = () => {
	const {
		localization: { language },
	} = useApplication();

	return (
		<>
			<Media lessThan="lg">
				<ProductQualityMobile {...ProductQualityContent[language]} />
			</Media>
			<Media greaterThanOrEqual="lg">
				<ProductQualityDesktop {...ProductQualityContent[language]} />
			</Media>
		</>
	);
};

export default ProductQuality;
